import { useState, useEffect } from 'react'
import { Plus, Edit2, ToggleLeft, ToggleRight, Trash2, Users, Star } from 'lucide-react'
import toast from 'react-hot-toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import promotoresService from '../../services/promotoresService'

export default function PromotoresPage() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'
  // Nomeado "Promotor" mesmo no fluxo cavalo — não confundir com o módulo
  // "Assessores" (por lote, cadastro separado em /assessores).
  const label = 'Promotor'
  const labelPlural = isFluxoCavalo ? 'Promotores (Leilão)' : 'Promotores'

  const [promotores, setPromotores] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState({ nome: '', telefone: '', cpf_cnpj: '', perc_venda: '', perc_defesa: '', is_padrao: false })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (orgId) carregar()
  }, [orgId])

  const carregar = async () => {
    setLoading(true)
    try {
      const data = await promotoresService.listar(orgId)
      setPromotores(data)
    } catch { }
    finally { setLoading(false) }
  }

  const abrirNovo = () => {
    setEditando(null)
    setForm({ nome: '', telefone: '', cpf_cnpj: '', perc_venda: '', perc_defesa: '', is_padrao: false })
    setShowModal(true)
  }

  const abrirEditar = (p) => {
    setEditando(p)
    setForm({
      nome: p.nome,
      telefone: p.telefone || '',
      cpf_cnpj: p.cpf_cnpj || '',
      perc_venda: String(p.perc_venda || ''),
      perc_defesa: String(p.perc_defesa || ''),
      is_padrao: p.is_padrao,
    })
    setShowModal(true)
  }

  const handleSalvar = async () => {
    if (!form.nome.trim()) { toast.error('Informe o nome'); return }
    setSaving(true)
    try {
      const payload = {
        nome: form.nome.trim(),
        telefone: form.telefone || null,
        cpf_cnpj: form.cpf_cnpj || null,
        perc_venda: parseFloat(form.perc_venda) || 0,
        perc_defesa: parseFloat(form.perc_defesa) || 0,
        is_padrao: form.is_padrao,
      }
      if (editando) {
        await promotoresService.atualizar(orgId, editando.id, payload)
        toast.success(`${label} atualizado`)
      } else {
        await promotoresService.criar(orgId, payload)
        toast.success(`${label} criado`)
      }
      setShowModal(false)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  const handleToggle = async (p) => {
    try {
      await promotoresService.toggle(orgId, p.id)
      toast.success(p.ativo ? `${label} inativado` : `${label} ativado`)
      carregar()
    } catch { toast.error('Erro ao alterar status') }
  }

  const handleExcluir = async (p) => {
    if (!confirm(`Excluir ${label.toLowerCase()} "${p.nome}"?`)) return
    try {
      await promotoresService.excluir(orgId, p.id)
      toast.success(`${label} excluído`)
      carregar()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir')
    }
  }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-indigo-600" />
          <h1 className="text-lg font-semibold text-gray-800">{labelPlural}</h1>
        </div>
        <button onClick={abrirNovo}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" /> Novo {label}
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-gray-400">Carregando...</div>
        ) : promotores.length === 0 ? (
          <div className="text-center py-8 text-gray-400">Nenhum {label.toLowerCase()} cadastrado.</div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {promotores.map(p => (
                <div key={p.id} className={`p-4 ${!p.ativo ? 'opacity-50' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        {p.is_padrao && <Star className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" fill="currentColor" />}
                        <span className="font-medium text-gray-800 truncate">{p.nome}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        {p.telefone && <span>{p.telefone}</span>}
                        {p.cpf_cnpj && <span>{p.cpf_cnpj}</span>}
                        <span className="font-medium text-indigo-700">V:{p.perc_venda}%</span>
                        <span className="font-medium text-orange-600">D:{p.perc_defesa}%</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${p.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {p.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                      <button onClick={() => abrirEditar(p)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleToggle(p)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg">
                        {p.ativo ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleExcluir(p)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Telefone</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">CPF/CNPJ</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">% Venda</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">% Defesa</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {promotores.map(p => (
                    <tr key={p.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!p.ativo ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {p.nome}
                        {p.is_padrao && <Star className="w-3.5 h-3.5 text-amber-500 inline ml-1.5" fill="currentColor" />}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{p.telefone || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{p.cpf_cnpj || '—'}</td>
                      <td className="px-4 py-3 text-center font-semibold text-indigo-700">{p.perc_venda}%</td>
                      <td className="px-4 py-3 text-center font-semibold text-orange-600">{p.perc_defesa}%</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${p.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {p.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => abrirEditar(p)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleToggle(p)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" title={p.ativo ? 'Inativar' : 'Ativar'}>
                            {p.ativo ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleExcluir(p)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal Criar/Editar */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">{editando ? `Editar ${label}` : `Novo ${label}`}</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 font-medium mb-1">Nome *</label>
                <input type="text" value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={`Nome do ${label.toLowerCase()}`} autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Telefone</label>
                  <input type="text" value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="(00) 00000-0000" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">CPF/CNPJ</label>
                  <input type="text" value={form.cpf_cnpj} onChange={e => setForm({ ...form, cpf_cnpj: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="000.000.000-00" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">% Venda</label>
                  <input type="number" step="0.01" min="0" max="100" value={form.perc_venda}
                    onChange={e => setForm({ ...form, perc_venda: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">% Defesa</label>
                  <input type="number" step="0.01" min="0" max="100" value={form.perc_defesa}
                    onChange={e => setForm({ ...form, perc_defesa: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="0.00" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_padrao} onChange={e => setForm({ ...form, is_padrao: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                <span className="text-sm text-gray-700">{label} padrão (pré-selecionado em novos leilões)</span>
              </label>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium">
                Cancelar
              </button>
              <button onClick={handleSalvar} disabled={saving}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {saving ? 'Salvando...' : editando ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
